// Copyright (c) 2021, 37.78 Tecnologia Ltda. All rights reserved.

import React, { useCallback, useEffect, useState } from "react";

import { useLazyQuery } from "@apollo/client";

import {
  isUserRegistered,
  logout,
  onIdTokenChanged,
} from "../../services/firebase";
import {
  addIntercom,
  bootIntercom,
  openIntercom,
  setOnHide,
  shutdownIntercom,
  updateIntercomUser,
} from "./intercom";
import { IntercomContext } from "./IntercomContext";
import patientActiveQuery from "./query";

export const IntercomProvider = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    (async () => {
      addIntercom();
    })();

    return () => {
      shutdownIntercom();
      setInitialized(false);
    };
  }, []);

  const openIfInitialized = async () => {
    if (initialized) {
      await openIntercom();
    }
  };

  const updateUser = useCallback(
    async (currentUser) => {
      if (initialized) {
        await updateIntercomUser(currentUser);
      }
    },
    [initialized]
  );

  const shutdown = useCallback(() => {
    if (initialized) {
      shutdownIntercom();
      setInitialized(false);
    }
  }, [initialized]);

  const openForCurrentUser = async (currentUser) => {
    await updateUser(currentUser);
    await openIfInitialized();
  };

  const [getPatientActive] = useLazyQuery(patientActiveQuery, {
    onCompleted: (data) => {
      if (data.patient?.[0]?.active === "true") {
        openForCurrentUser(userData);
      } else {
        shutdown();
        logout();
      }
    },
  });

  const open = (patientData) => {
    setUserData(patientData);
    getPatientActive();
  };

  useEffect(() => {
    const handleTokenUpdate = async (user) => {
      if (!user) {
        // logout
        shutdown();
        return;
      }

      const isRegistered = await isUserRegistered(user);
      if (!isRegistered) {
        // no info to send to intercom
        return;
      }

      if (!initialized) {
        await bootIntercom(user);
        setInitialized(true);
        return;
      }

      await updateUser(user);
    };
    return onIdTokenChanged(handleTokenUpdate);
  }, [initialized, shutdown, updateUser]);

  const value = {
    open,
    setOnHide,
    updateUser,
  };

  return (
    <IntercomContext.Provider value={value}>
      {children}
    </IntercomContext.Provider>
  );
};
